import {Component, inject, OnInit} from "@angular/core";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from "@angular/material/tree";
import {FlatTreeControl} from "@angular/cdk/tree";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatChipsModule} from "@angular/material/chips";
import {MatMenuModule} from "@angular/material/menu";
import {KnowledgeBaseService, NodeType} from "../data-access/knowledge-base.service";
import {UserService} from "src/app/services/user.service";
import {SolutionService} from "../data-access/solution.service";
import {HeaderService} from "src/app/services/header.service";
import {ModalService} from "src/app/shared/data-access/modal.service";
import {EMPTY, forkJoin, map, Observable, switchMap, tap} from "rxjs";
import {CookieService} from "ngx-cookie-service";
import {IncidentIdService} from "src/app/features/error-report/data-access/incident-id.service";
import {NodeDataService} from "src/app/services/node-data.service";
import {ReportService} from "src/app/features/error-report/data-access/report.service";
import {EditTreeItemService} from "../data-access/edit-tree-item.service";
import {Userrole} from "src/app/shared/interfaces/user";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {ApiService, Tables} from "../../../services/api.service";
import {LinkedReportsDialogComponent} from "../linked-reports-dialog/linked-reports-dialog.component";
import {Report} from "../../../shared/interfaces/report";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {NotificationService} from "../../../shared/data-access/notification.service";
import {
  ConfirmationDialogComponent,
  DialogData
} from "../../../shared/confirmation-dialog/confirmation-dialog.component";
import {MoveItemDialogComponent} from "../move-item-dialog/move-item-dialog.component";
import {Category} from "../../../shared/interfaces/category";
import {Malfunction} from "../../../shared/interfaces/malfunction";
import {Solution} from "../../../shared/interfaces/solution";

export interface TreeNode {
  id: string;
  name: string;
  type:
    | "productionLine"
    | "workplace"
    | "category"
    | "malfunction"
    | "solution"
    | "button";
  level?: NodeLevel;
  expandable?: boolean;
  children?: TreeNode[];
  parentId?: string;
  data?: any[];
}

type AtLeastOne<T> = {
  [K in keyof T]: Pick<T, K> & Partial<Omit<T, K>>
}[keyof T];

export interface MoveData {
  nodeId: string,
  nodeType: Omit<NodeType, 'button'>,
  newParentIds: AtLeastOne<{ categoryId: string, malfunctionId: string }>,
}

export enum NodeLevel {
  CATEGORY = 0,
  MALFUNCTION = 1,
  SOLUTION = 2,
}

interface FlatTreeNode {
  id: string;
  name: string;
  type:
    | "productionLine"
    | "workplace"
    | "category"
    | "malfunction"
    | "solution"
    | "button";
  level: number;
  parentId?: string;
  expandable: boolean;
}

@Component({
  selector: "app-kd-workplace",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTreeModule,
    MatIconModule,
    MatButtonModule,
    MatExpansionModule,
    MatChipsModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: "./kd-workplace.component.html",
  styleUrls: ["./kd-workplace.component.scss"],
})
export class KdWorkplaceComponent {
  private apiService = inject(ApiService);
  knowledgeBaseService = inject(KnowledgeBaseService);
  solutionService = inject(SolutionService);
  userService = inject(UserService);
  headerService = inject(HeaderService);
  modalService = inject(ModalService);
  cookieService = inject(CookieService);
  incidentService = inject(IncidentIdService);
  nodeDataService = inject(NodeDataService);
  editTreeItemService = inject(EditTreeItemService);
  reportService = inject(ReportService);
  private notification = inject(NotificationService);

  Userrole = Userrole;
  NodeLevel = NodeLevel;

  private dialog = inject(MatDialog);

  private transformer = (node: TreeNode, level: number): FlatTreeNode => {
    return {
      id: node.id,
      name: node.name,
      type: node.type,
      level: level,
      parentId: node.parentId,
      expandable: node.type !== "button",
    };
  };

  treeControl = new FlatTreeControl<FlatTreeNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  expandedNodes = new Set<TreeNode>();

  private getExpandedNodeIds(): string[] {
    return this.treeControl.expansionModel.selected.map((node) => node.id);
  }

  private restoreExpandedNodes(expandedNodeIds: string[]) {
    const nodesToExpand = this.treeControl.dataNodes.filter((node) =>
      expandedNodeIds.includes(node.id)
    );
    nodesToExpand.forEach((node) => this.treeControl.expand(node));
  }

  constructor() {
    this.headerService.setHeaderSubTitle(
      `${this.knowledgeBaseService.expandedNodes().productionLine?.name} - ${
        this.knowledgeBaseService.expandedNodes().workplace?.name
      }`
    );
    this.knowledgeBaseService.treeData$
      .pipe(map((data: TreeNode[]) => {
          if (!(this.userService.userrole() === Userrole.Mitarbeiter && this.knowledgeBaseService.activeModule() === 'knowledgeBase')) {
            return this.addAddButtonsToTreeData(data)
          } else {
            return data
          }
        })
      )
      .subscribe((data) => {
        this.dataSource.data = data;
        this.restoreExpandedNodes(
          this.knowledgeBaseService.expandedNodeIdsSignal()
        );
      });
  }

  hasChild = (_: number, node: TreeNode) =>
    node.expandable && node.type !== "button";

  private hasChildren(node: TreeNode): boolean {
    return node.children !== undefined &&
      node.children.length > 0 &&
      node.children.some(child => child.type !== "button");
  }

  isAddButton = (_: number | string, node: FlatTreeNode): boolean =>
    node.name === "ADD_BUTTON";

  addAddButtonsToTreeData(treeData: TreeNode[]): TreeNode[] {
    const processLevel = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children) {
          const hasAddButton = node.children.some(
            (child) => child.name === "ADD_BUTTON" && child.type === "button"
          );

          processLevel(node.children);

          if (!hasAddButton) {
            node.children.push({
              id: "add_button",
              name: "ADD_BUTTON",
              level: node.level! + 1,
              type: "button",
              expandable: false,
              parentId: node.id,
            });
          }
        }
      });
    };
    processLevel(treeData);

    const hasRootButton = treeData.some(
      (node) => node.name === "ADD_BUTTON" && node.type === "button"
    );

    if (!hasRootButton) {
      treeData.push({
        id: "add_button_root",
        name: "ADD_BUTTON",
        level: 0,
        type: "button",
        expandable: false,
      });
    }
    return treeData;
  }

  loadChildren(node: FlatTreeNode) {
    switch (node.level) {
      case 0:
        // get Malfunctions for Process step
        this.knowledgeBaseService.selectCategory(
          node.id,
          this.getExpandedNodeIds()
        );
        this.knowledgeBaseService.updateSelectedItems("category", node);
        this.knowledgeBaseService.updateSelectedItems("malfunction", null);
        break;
      case 1:
        // get Solutions for Malfunction
        this.knowledgeBaseService.selectMalfunction(
          node.id,
          this.getExpandedNodeIds()
        );
        this.knowledgeBaseService.updateSelectedItems("malfunction", node);
        this.knowledgeBaseService.updateSelectedItems("solution", null);
        break;
      case 2:
        this.knowledgeBaseService.updateSelectedItems("solution", node);
        this.knowledgeBaseService.changeActivePage(2);
        this.reportService.update$.next({
          properties: {
            solution: node.id
          }
        })
        this.solutionService.selectedSolutionId$.next(node.id);

      // change active page to solution component
    }
  }

  getActionForNodeLevel(level: number): string {
    switch (level) {
      case 0:
        return 'Neuen Prozessschritt';
      case 1:
        return "Neue Störung";
      case 2:
        return "Neue Lösung"
      default:
        return "Eintrag"
    }
  }

  collapseNode(node: FlatTreeNode) {
    switch (node.level) {
      case 0:
        this.knowledgeBaseService.updateSelectedItems("category", null);
        this.knowledgeBaseService.updateSelectedItems("malfunction", null);
        break;
      case 1:
        this.knowledgeBaseService.updateSelectedItems("malfunction", null);
    }
  }

  transformToTreeNodes(data: any[], nodeType: string): TreeNode[] {
    return data.map((item) => ({
      id: item.id,
      name: item.name,
      type: nodeType as any,
      children: [],
      data: item.data,
    }));
  }

  back() {
    this.knowledgeBaseService.goBack();
  }

  openMenu(event: Event) {
    event.stopPropagation();
  }

  addNode(level: 0 | 1 | 2) {
    this.knowledgeBaseService.treeInputLevel.set(level);
    switch (level) {
      case 0:
        this.editTreeItemService.action.set("addCategory");
        this.editTreeItemService.modalHeading$.next(
          "Prozessschritt hinzufügen"
        );
        break;
      case 1:
        this.editTreeItemService.action.set("addMalfunction");
        this.editTreeItemService.modalHeading$.next("Neue Störung hinzufügen");

        break;
      case 2:
        this.editTreeItemService.action.set("addSolution");
        this.editTreeItemService.modalHeading$.next("Neue Lösung hinzufügen");

        break;
    }
    this.modalService.openModal("itemFormModal");
  }

  editNode(node: TreeNode) {
    this.editTreeItemService.itemBeingEdited$.next(node);
    this.modalService.openModal("itemFormModal");
  }

  deleteNode(node: TreeNode) {
    const dialogData: DialogData = {
      message: `Möchten Sie den Eintrag "${node.name}" wirklich löschen?`,
      name: "Eintrag löschen",
      actions: ['cancel', 'confirm']
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: dialogData
    })

    dialogRef.afterClosed().pipe(
      tap((result) => console.log(result)),
      switchMap(result => {
        if (result) {
          return this.knowledgeBaseService.deleteItem(node);
        }
        return EMPTY;
      })
    ).subscribe({
      next: (deletedItem) => {
        if (deletedItem) {
          this.notification.showNotification('Eintrag erfolgreich gelöscht', 3000);
        }
      },
      error: (error) => {
        if (error.message.includes('untergeordnete Elemente')) {
          this.notification.showNotification('Dieser Eintrag kann nicht gelöscht werden, da er untergeordnete Elemente enthält.', 5000);
        } else {
          this.notification.showNotification('Fehler beim Löschen des Eintrags', 3000);
        }
      }
    });
  }

  moveNode(node: TreeNode) {
    this.openMoveItemDialog(node, this.knowledgeBaseService.expandedNodes())
  }

  private openMoveItemDialog(node: TreeNode, expandedNodes: {
    productionLine: TreeNode | null;
    workplace: TreeNode | null;
    category: TreeNode | null;
    malfunction: TreeNode | null;
    solution: TreeNode | null;
  }) {
    const dialogRef = this.dialog.open(MoveItemDialogComponent, {
      width: '800px',
      maxHeight: '600px',
      data: {node, expandedNodes}
    })

    dialogRef.afterClosed().pipe(
      tap((moveData) => console.log(moveData)),
      switchMap((moveData: MoveData) => {
        if (moveData) {
          return this.changeParentItem(moveData);
        }
        return EMPTY;
      })
    ).subscribe({
      next: (updatedItem) => {
        if (updatedItem) {
          this.notification.showNotification('Eintrag wurde erfolgreich verschoben', 3000);
        }
        this.treeControl.collapseAll();

      },
      error: (error) => {
        console.error(error);
        this.notification.showNotification('Es ist ein Fehler aufgetreten', 3000);
      }
    });
  }

  getLinkedReports(id: string) {
    this.apiService.getReportsBySolutionId(id).subscribe({
      next: (reports) => {
        this.openLinkedReportsDialog(reports);
      },
      error: (error) => {
        console.error("Error fetching linked reports:", error);
      }
    })
  }

  private openLinkedReportsDialog(reports: Report[]) {
    this.dialog.open(LinkedReportsDialogComponent, {
      width: '800px',
      maxHeight: '600px',
      data: {reports}
    })
  }

  changeParentItem(moveData: MoveData): Observable<any> {
    this.updateReportsAfterItemMove(moveData);
    if (moveData.nodeType === 'malfunction') {
      return this.apiService.updateItemInKnowledgeBase(Tables.Malfunction, {
        id: moveData.nodeId,
        'category': moveData.newParentIds.categoryId
      })
    } else {
      return this.apiService.updateItemInKnowledgeBase(Tables.Solution, {
        id: moveData.nodeId,
        'malfunction': moveData.newParentIds.malfunctionId
      })
    }


  }

  updateReportsAfterItemMove(moveData: MoveData) {
    // migrate report data
    // get all reports with moved solution or malfunction
    // for solution: update malfunction and category
    // for malfunction: update category
    if (moveData.nodeType === 'solution') {
      this.apiService.getReportsBySolutionId(moveData.nodeId).pipe(
        tap((reports: Report[]) => console.log('reports to update', reports)),
        switchMap((reports: Report[]) => {
          if (reports.length === 0) {
            return EMPTY;
          }
          const updatedRequests = reports.map(report => {
            return this.apiService.updateAndReturnReport({
              id: report.id,
              category: moveData.newParentIds.categoryId,
              malfunction: moveData.newParentIds.malfunctionId
            })
          })
          return forkJoin(updatedRequests);
        })
      ).subscribe({
        next: (updatedReports) => {
          if (updatedReports && updatedReports.length > 0) {
            this.notification.showNotification(
              `${updatedReports.length} Stillstandsmeldungen erfolgreich aktualisiert`,
              3000
            );
          }
        },
        error: (error) => {
          console.error('Fehler beim Aktualisieren der Stillstandsmeldungen:', error);
          this.notification.showNotification(
            'Fehler beim Aktualisieren der Stillstandsmeldungen',
            3000
          );
        }
      });
    } else if (moveData.nodeType === 'malfunction') {
      this.apiService.getReportsByMalfunctionId(moveData.nodeId).pipe(
        tap((reports: Report[]) => console.log('reports to update', reports)),
        switchMap((reports: Report[]) => {
          if (reports.length === 0) {
            return EMPTY;
          }
          const updatedRequests = reports.map(report => {
            return this.apiService.updateAndReturnReport({
              id: report.id,
              category: moveData.newParentIds.categoryId
            })
          })
          return forkJoin(updatedRequests);
        })
      ).subscribe({
        next: (updatedReports) => {
          if (updatedReports && updatedReports.length > 0) {
            this.notification.showNotification(
              `${updatedReports.length} Stillstandsmeldungen erfolgreich aktualisiert`,
              3000
            );
          }
        },
        error: (error) => {
          console.error('Fehler beim Aktualisieren der Stillstandsmeldungen:', error);
          this.notification.showNotification(
            'Fehler beim Aktualisieren der Stillstandsmeldungen',
            3000
          );
        }
      });
    }
  }

  createNewEscalation() {
    this.knowledgeBaseService.addItem({
      name: '',
      description: this.solutionService.NO_SOLUTION_AVAILABLE,
    }, 'addSolution').subscribe((solution) => {
      this.solutionService.selectedSolutionId$.next(solution.id)
      this.modalService.openModal('escalationModal')
    })
  }
}
